/**
 * Heatmap Service — Operational Load Density by Day × Hour
 *
 * v2: Rich task data — active tasks per slot derived from started_at/completed_at
 * range intersection, with dominant stage, responsáveis and queue time.
 */

import { supabase } from '../lib/supabaseClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** 0 = Sunday … 6 = Saturday */
export type DayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** A single task row from the DB (raw) */
export interface HeatmapTaskRow {
  id: string;
  stage_name: string | null;
  assigned_to: string | null;
  started_at: string | null;
  completed_at: string | null;
  queue_duration: number | null;
  planned_duration: number | null;
  actual_duration: number | null;
}

export interface HeatmapCell {
  day: DayIndex;
  hour: number;       // 0–23
  count: number;
  efficiency: number | null;
  /** 0–1, normalized relative to the busiest cell */
  intensity: number;
  /** Lean intensity tier: 0=empty 1=low 2=med 3=high */
  tier: 0 | 1 | 2 | 3;
  /** Most frequent stage in this slot */
  dominantStage: string | null;
  /** Top-2 responsáveis (names or IDs) */
  topAssignees: string[];
  /** Average queue_duration (minutes) of tasks active in this slot */
  avgQueueMinutes: number | null;
  /** Task IDs active in this slot — for drill-down */
  taskIds: string[];
}

export interface HeatmapMatrix {
  cells: HeatmapCell[];
  maxCount: number;
  totalTasks: number;
  /** Most loaded slot */
  peakSlot: { day: DayIndex; hour: number; count: number; dominantStage: string | null } | null;
  /** Auto-generated insight string */
  insight: string | null;
  /** Raw rows for drill-down filtering */
  rows: HeatmapTaskRow[];
}

export interface HeatmapFilters {
  organizationId: string;
  startDate: string;  // ISO
  endDate: string;    // ISO
}

// ---------------------------------------------------------------------------
// Capacity types (Yamazumi ↔ Heatmap integration)
// ---------------------------------------------------------------------------

export interface CapacityConfig {
  /** Total tasks (or task-slots) the team can handle per hour */
  totalCapacityPerHour: number;
  /** Number of hours in the work shift (used for display) */
  shiftHours: number;
  /** Total FTE configured */
  fte: number;
  /** Label of the most loaded role, for tooltip */
  topRoleLabel?: string;
}

/** Per-cell gap result */
export interface CellGap {
  /** load - capacity: negative = folga, positive = sobrecarga */
  gap: number;
  /** 'ok' | 'limit' | 'risk' | 'overload' */
  level: 'ok' | 'limit' | 'risk' | 'overload';
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const DAY_LABELS: Record<DayIndex, string> = {
  0: 'Dom',
  1: 'Seg',
  2: 'Ter',
  3: 'Qua',
  4: 'Qui',
  5: 'Sex',
  6: 'Sáb',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns Lean intensity tier based on task count */
function calcTier(count: number): 0 | 1 | 2 | 3 {
  if (count === 0) return 0;
  if (count <= 2) return 1;
  if (count <= 5) return 2;
  return 3;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export async function getHeatmapMatrix(filters: HeatmapFilters): Promise<HeatmapMatrix> {
  const { organizationId, startDate, endDate } = filters;

  // Fetch tasks that were active (started but not yet completed) in the range
  // A task is "active at slot [D,H]" if started_at <= slot_end AND (completed_at IS NULL OR completed_at >= slot_start)
  // We pull all tasks that overlap the date window and do slot matching client-side.
  const { data, error } = await supabase
    .from('workflow_tasks_v9')
    .select('id, stage_name, assigned_to, started_at, completed_at, queue_duration, planned_duration, actual_duration')
    .eq('organization_id', organizationId)
    .not('started_at', 'is', null)
    .gte('started_at', startDate)
    .lte('started_at', endDate)
    .limit(10_000);

  if (error) {
    if ((error as any).code === '42703' || String(error.message).includes('column')) {
      console.warn('[heatmapService] Schema mismatch — check migration.');
      return { cells: [], totalTasks: 0, maxCount: 1, peakSlot: null, insight: null, rows: [] };
    }
    throw error;
  }

  const rows: HeatmapTaskRow[] = (data ?? []) as HeatmapTaskRow[];

  // --- Aggregate into 7 × 24 buckets ---
  type Bucket = {
    taskIds: string[];
    effSum: number; effN: number;
    stages: Record<string, number>;
    assignees: Record<string, number>;
    queueSum: number; queueN: number;
  };

  const matrix: Bucket[][] = Array.from({ length: 7 }, () =>
    Array.from({ length: 24 }, () => ({
      taskIds: [], effSum: 0, effN: 0,
      stages: {}, assignees: {}, queueSum: 0, queueN: 0,
    }))
  );

  for (const row of rows) {
    if (!row.started_at) continue;
    const startTs = new Date(row.started_at);
    const endTs = row.completed_at ? new Date(row.completed_at) : new Date(startTs.getTime() + 60 * 60 * 1000); // assume 1h if open

    // Walk each hour slot this task was active
    const cursor = new Date(startTs);
    cursor.setMinutes(0, 0, 0); // align to hour start
    let steps = 0;
    while (cursor <= endTs && steps < 48) {
      steps++;
      const day = cursor.getDay() as DayIndex;
      const hour = cursor.getHours();
      const b = matrix[day][hour];
      b.taskIds.push(row.id);
      if (row.stage_name) b.stages[row.stage_name] = (b.stages[row.stage_name] ?? 0) + 1;
      if (row.assigned_to) b.assignees[row.assigned_to] = (b.assignees[row.assigned_to] ?? 0) + 1;
      if (row.queue_duration != null) { b.queueSum += row.queue_duration; b.queueN++; }
      if (row.planned_duration != null && row.actual_duration != null && Number(row.actual_duration) > 0) {
        b.effSum += Number(row.planned_duration) / Number(row.actual_duration);
        b.effN++;
      }
      cursor.setHours(cursor.getHours() + 1);
    }
  }

  // --- Flatten and normalize ---
  let maxCount = 1;
  const rawCells: (Omit<HeatmapCell, 'intensity' | 'tier'> & { count: number })[] = [];

  for (let d = 0; d < 7; d++) {
    for (let h = 0; h < 24; h++) {
      const b = matrix[d][h];
      const count = b.taskIds.length;
      if (count > maxCount) maxCount = count;
      const topStages = Object.entries(b.stages).sort((a, z) => z[1] - a[1]);
      const topAsgn = Object.entries(b.assignees).sort((a, z) => z[1] - a[1]).slice(0, 2).map(e => e[0]);
      rawCells.push({
        day: d as DayIndex,
        hour: h,
        count,
        efficiency: b.effN > 0 ? b.effSum / b.effN : null,
        dominantStage: topStages[0]?.[0] ?? null,
        topAssignees: topAsgn,
        avgQueueMinutes: b.queueN > 0 ? Math.round(b.queueSum / b.queueN) : null,
        taskIds: [...new Set(b.taskIds)],
      });
    }
  }

  const cells: HeatmapCell[] = rawCells.map(c => ({
    ...c,
    intensity: maxCount > 0 ? c.count / maxCount : 0,
    tier: calcTier(c.count),
  }));

  const peak = cells.reduce<HeatmapCell | null>(
    (best, c) => (!best || c.count > best.count ? c : best),
    null
  );

  // --- Auto-insight ---
  let insight: string | null = null;
  if (peak && peak.count > 0) {
    const dayLabel = DAY_LABELS[peak.day];
    const hourLabel = `${peak.hour.toString().padStart(2, '0')}h`;
    const stageLabel = peak.dominantStage ? ` — gargalo em "${peak.dominantStage}"` : '';
    insight = `Pico às ${hourLabel} (${dayLabel})${stageLabel} · ${peak.count} tarefa${peak.count !== 1 ? 's' : ''} simultânea${peak.count !== 1 ? 's' : ''}`;
  }

  return {
    cells,
    maxCount,
    totalTasks: rows.length,
    peakSlot: peak ? { day: peak.day, hour: peak.hour, count: peak.count, dominantStage: peak.dominantStage } : null,
    insight,
    rows,
  };
}

// ---------------------------------------------------------------------------
// Operational Score
// ---------------------------------------------------------------------------

export interface OperationalScore {
  /** 0–100 composite score */
  score: number;
  /** 'excellent' ≥85  'good' ≥65  'warning' ≥40  'critical' <40 */
  level: 'excellent' | 'good' | 'warning' | 'critical';
  label: string;
  breakdown: {
    slaContrib: number;
    bottleneckPenalty: number;
    overloadPenalty: number;
    critProblemPenalty: number;
  };
}

export function calcularScoreOperacional(params: {
  slaRiskPercent: number;
  bottleneckCount: number;
  overloadCount: number;
  criticalProblems: number;
}): OperationalScore {
  const { slaRiskPercent, bottleneckCount, overloadCount, criticalProblems } = params;

  const base = 100;
  const slaContrib = slaRiskPercent * 0.6;
  const bottleneckPenalty = bottleneckCount * 4;
  const overloadPenalty = overloadCount * 5;
  const critProblemPenalty = criticalProblems * 15;

  const raw = base - slaContrib - bottleneckPenalty - overloadPenalty - critProblemPenalty;
  const score = Math.round(Math.max(0, Math.min(100, raw)));

  const level: OperationalScore['level'] =
    score >= 85 ? 'excellent' :
    score >= 65 ? 'good' :
    score >= 40 ? 'warning' :
    'critical';

  const label =
    level === 'excellent' ? 'Excelente' :
    level === 'good' ? 'Bom' :
    level === 'warning' ? 'Atenção' :
    'Crítico';

  return { score, level, label, breakdown: { slaContrib, bottleneckPenalty, overloadPenalty, critProblemPenalty } };
}
